import { storeMoney } from "@libs/platform-wire";

export type MusicAccessBenefitTier = { label: string };

export type MusicAccessGemEconomicsQuote = {
  eligible: boolean;
  ineligibilityReason?: string | null;
  applied: boolean;
  optedOut: boolean;
  purchasePriceMinorUnits: number;
  amountDueMinorUnits: number;
  availableDiscountMinorUnits: number;
  appliedDiscountMinorUnits: number;
  availableGemQuantity: number;
  gemQuantity: number;
  spendableGemsConsidered: number;
  artistStatus: { benefitTier: MusicAccessBenefitTier };
  platformStatus: { benefitTier: MusicAccessBenefitTier };
  display: {
    status: string;
    amountDue: string;
    discount: string | null;
    gemSpend: string | null;
  };
};

export type CheckoutPaymentPlatform =
  "ios" | "android" | "web" | "macos" | "windows" | "native";

/** Pence, as the house style says pounds short (`storeMoney`): "£18",
 *  "£18.50", "£1,234", "-£1". */
export function formatGbpMinorUnits(minorUnits: number): string {
  // `+ 0`: a discount of nothing, negated (-0), is "£0", never "-£0".
  return storeMoney(Math.round(minorUnits) / 100 + 0, "GBP", { compact: true });
}

/** A music access's price in pounds, short ("£18", "£18.50"), or "FREE". */
export function formatMusicAccessPrice(amount: number): string {
  if (amount <= 0) return "FREE";
  return storeMoney(amount, "GBP", { compact: true });
}

export function formatGemQuantity(gems: number): string {
  return Math.trunc(gems).toLocaleString("en-GB");
}

export function getCheckoutPaymentLabel(
  platform: CheckoutPaymentPlatform,
): string {
  if (platform === "ios") return "Card or Apple Pay";
  if (platform === "android") return "Card or Google Pay";
  return "Card payment";
}

export function getStatusDisplayName(
  quote: MusicAccessGemEconomicsQuote,
): string {
  const artistStatus = quote.artistStatus.benefitTier.label.trim();
  const displayStatus = quote.display.status.trim().replace(/\s+rate$/i, "");
  const platformStatus = quote.platformStatus.benefitTier.label.trim();
  return artistStatus || displayStatus || platformStatus || "status";
}

export function getStatusPriceLabel(
  quote?: MusicAccessGemEconomicsQuote | null,
): string | null {
  return quote ? `${getStatusDisplayName(quote)} price` : null;
}

export function getGemStatusRateText(
  quote: MusicAccessGemEconomicsQuote,
): string {
  return quote.display.status.replace(/\s+\+\s+/g, " ");
}

export function getGemCalculationText(
  quote: MusicAccessGemEconomicsQuote,
  useGemsSelected = quote.applied,
): string {
  const gemQuantity = useGemsSelected
    ? quote.gemQuantity || quote.availableGemQuantity
    : quote.availableGemQuantity;
  const gemLabel = gemQuantity === 1 ? "gem" : "gems";
  return `${getGemStatusRateText(quote)} · ${formatGemQuantity(gemQuantity)} ${gemLabel}`;
}

/** Shared native/LIB gate: an unavailable server quote never offers Gem payment. */
export function isGemPaymentAvailable(
  quote?: MusicAccessGemEconomicsQuote | null,
): boolean {
  return quote?.eligible === true && quote.availableDiscountMinorUnits > 0;
}

export function getCheckoutAmountBreakdown({
  quote,
  fallbackTotalMinorUnits,
  useGemsSelected = quote?.applied ?? true,
}: {
  quote?: MusicAccessGemEconomicsQuote | null;
  fallbackTotalMinorUnits: number;
  useGemsSelected?: boolean;
}) {
  const originalMinorUnits =
    quote?.purchasePriceMinorUnits ?? fallbackTotalMinorUnits;
  const appliedDiscountMinorUnits =
    quote &&
    quote.ineligibilityReason !== "engine_backed_access" &&
    useGemsSelected
      ? quote.applied
        ? quote.appliedDiscountMinorUnits
        : quote.availableDiscountMinorUnits
      : 0;
  const amountDueMinorUnits =
    quote &&
    quote.ineligibilityReason !== "engine_backed_access" &&
    quote.applied === useGemsSelected
      ? quote.amountDueMinorUnits
      : Math.max(0, originalMinorUnits - appliedDiscountMinorUnits);
  return {
    originalMinorUnits,
    amountDueMinorUnits,
    appliedDiscountMinorUnits,
    hasAppliedGemDiscount: appliedDiscountMinorUnits > 0,
    hasAvailableGemDiscount: isGemPaymentAvailable(quote),
    originalAmount: formatGbpMinorUnits(originalMinorUnits),
    amountDue: formatGbpMinorUnits(amountDueMinorUnits),
    gemDiscountAmount: formatGbpMinorUnits(-appliedDiscountMinorUnits),
  };
}

/** Preserve matching server totals; engine quotes always discard Gem discounts. */
export function createOptimisticMusicAccessGemQuote(input: {
  quote?: MusicAccessGemEconomicsQuote | null;
  subtotalMinorUnits: number;
  useGemsSelected: boolean;
}): MusicAccessGemEconomicsQuote | null {
  const { quote, subtotalMinorUnits, useGemsSelected } = input;
  if (!quote) return null;
  if (
    quote.ineligibilityReason !== "engine_backed_access" &&
    quote.purchasePriceMinorUnits === subtotalMinorUnits
  )
    return quote;

  const availableDiscountMinorUnits = Math.min(
    subtotalMinorUnits,
    quote.eligible ? quote.availableDiscountMinorUnits : 0,
  );
  const appliedDiscountMinorUnits = useGemsSelected
    ? availableDiscountMinorUnits
    : 0;
  const discountRatio =
    quote.availableDiscountMinorUnits > 0
      ? availableDiscountMinorUnits / quote.availableDiscountMinorUnits
      : 0;
  const availableGemQuantity =
    discountRatio > 0
      ? Math.max(1, Math.ceil(quote.availableGemQuantity * discountRatio))
      : 0;
  const gemQuantity = useGemsSelected ? availableGemQuantity : 0;
  const amountDueMinorUnits = Math.max(
    0,
    subtotalMinorUnits - appliedDiscountMinorUnits,
  );

  return {
    ...quote,
    applied: useGemsSelected && availableDiscountMinorUnits > 0,
    optedOut: !useGemsSelected,
    purchasePriceMinorUnits: subtotalMinorUnits,
    amountDueMinorUnits,
    availableDiscountMinorUnits,
    appliedDiscountMinorUnits,
    availableGemQuantity,
    gemQuantity,
    display: {
      ...quote.display,
      amountDue: formatGbpMinorUnits(amountDueMinorUnits),
      discount:
        availableDiscountMinorUnits > 0
          ? formatGbpMinorUnits(availableDiscountMinorUnits)
          : null,
      gemSpend:
        availableGemQuantity > 0
          ? `${formatGemQuantity(availableGemQuantity)} Gems = ${formatGbpMinorUnits(availableDiscountMinorUnits)}`
          : null,
    },
  };
}

/**
 * Why music could not be unlocked, in the buyer's words, and whether another
 * try could help (LIS-1038); null when the error says no more than that it
 * failed. Reads both clients' refusals: the core API's (the code as the
 * tRPC error's message, or NOT_FOUND) and Link in Bio's (the code as the
 * error's message).
 */
export function musicUnlockRefusal(
  error: unknown,
): { message: string; retry: boolean } | null {
  const refusal = error as {
    message?: unknown;
    data?: { code?: unknown };
  } | null;
  if (refusal?.message === "invalid_unlock_code")
    return { message: "That unlock code didn't work.", retry: false };
  if (
    refusal?.message === "unavailable" ||
    refusal?.message === "not_found" ||
    refusal?.data?.code === "NOT_FOUND"
  )
    return { message: "This isn't available right now.", retry: false };
  return null;
}

/** A music access's price, in major units, in its currency's minor units
 *  (as the engine sells it). */
export function musicPriceMinor(price: number, currency: string) {
  let digits = 2;
  try {
    digits =
      new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    /* An unknown code: two places, as most currencies have. */
  }
  return Math.round(price * 10 ** digits);
}
